import { cn } from "@/lib/utils";
import { useHideSunday } from "@/hooks/use-user-preferences";
import {
  buildEventColorTheme,
  getMonthMatrix,
  getWeekdayHeadersTwo,
  isSameDay,
  isToday,
  toDateKey,
  weekdayGridColsClass,
} from "./calendar-utils";

const EVENT_DOT_COLOR_CLASSES = {
  blue: "bg-blue-500",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  purple: "bg-purple-500",
  pink: "bg-pink-500",
  orange: "bg-orange-500",
  cyan: "bg-cyan-500",
  rose: "bg-rose-500",
  amber: "bg-amber-500",
};

function MiniMonthCalendar({
  selectedDate,
  eventsByDate = {},
  selectedTenantIds = [],
  tenantFilterEnabled = false,
  selectedDoctorIds = [],
  doctorFilterEnabled = false,
  onSelectDate,
  onNavigateMonth,
  className,
}) {
  const hideSunday = useHideSunday();
  const monthCells = getMonthMatrix(selectedDate, { hideSunday });
  const weekdayHeaders = getWeekdayHeadersTwo({ hideSunday });
  const colsClass = weekdayGridColsClass(hideSunday);
  const selectedTenantIdSet = new Set(selectedTenantIds);
  const selectedDoctorIdSet = new Set(selectedDoctorIds);

  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center text-center text-foreground">
        <button
          type="button"
          onClick={() => onNavigateMonth?.(-1)}
          className="flex flex-none items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <span className="sr-only">Previous month</span>
          <span className="text-xl leading-none">{"<"}</span>
        </button>
        <div className="flex-auto text-sm font-semibold">{selectedDate.toFormat("LLLL yyyy")}</div>
        <button
          type="button"
          onClick={() => onNavigateMonth?.(1)}
          className="flex flex-none items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <span className="sr-only">Next month</span>
          <span className="text-xl leading-none">{">"}</span>
        </button>
      </div>

      <div className={cn("mt-6 grid text-center text-xs/6 text-muted-foreground", colsClass)}>
        {weekdayHeaders.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>

      <div className={cn("isolate mt-2 grid gap-px overflow-hidden rounded-lg border border-border/60 bg-muted/50 text-sm shadow-sm", colsClass)}>
        {monthCells.map((cell) => {
          const eventsForDay = eventsByDate?.[toDateKey(cell.date)] || [];
          const dayEvents = eventsForDay.filter((event) => {
            if (tenantFilterEnabled) {
              const tenantId = String(event?.tenantId || "").trim();
              if (!selectedTenantIdSet.has(tenantId)) {
                return false;
              }
            }
            if (doctorFilterEnabled) {
              const doctorId = String(event?.doctorId || "").trim();
              if (!selectedDoctorIdSet.has(doctorId)) {
                return false;
              }
            }
            return true;
          });
          const uniqueColorEvents = dayEvents.filter(
            (event, index, source) =>
              source.findIndex((entry) => `${entry.colorHex || ""}:${entry.color || ""}` === `${event.colorHex || ""}:${event.color || ""}`) ===
              index,
          );

          return (
            <button
              key={toDateKey(cell.date)}
              type="button"
              onClick={() => onSelectDate?.(cell.date)}
              className={cn(
                "py-1.5 hover:bg-muted",
                cell.isCurrentMonth
                  ? "bg-background text-foreground"
                  : "bg-muted/30 text-muted-foreground",
              )}
            >
              <time
                dateTime={toDateKey(cell.date)}
                className={cn(
                  "mx-auto flex size-7 items-center justify-center rounded-full",
                  isSameDay(cell.date, selectedDate) &&
                    !isToday(cell.date) &&
                    "bg-foreground text-background",
                  isToday(cell.date) && "bg-primary text-primary-foreground",
                )}
              >
                {cell.date.day}
              </time>
              {dayEvents.length > 0 ? (
                <span className="mt-1 flex justify-center gap-1">
                  {uniqueColorEvents.slice(0, 3).map((event) => (
                    <span
                      key={event.id}
                      style={buildEventColorTheme(event.colorHex) || undefined}
                      className={cn(
                        "size-1.5 rounded-full",
                        event.colorHex
                          ? "bg-(--event-dot-light) dark:bg-(--event-dot-dark)"
                          : EVENT_DOT_COLOR_CLASSES[event.color] || "bg-muted-foreground",
                      )}
                    />
                  ))}
                </span>
              ) : (
                <span className="mt-1 block h-1.5" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default MiniMonthCalendar;
